/**
 * Ask Melio — all client transport in one place.
 *
 * THIS FILE IS THE TRANSPORT SEAM. Everything the chat does over the wire goes
 * through here, so the Phase-3 move to streaming replies (bump
 * firebase-functions to v6, switch to onCall streaming + client .stream()) is a
 * change to this file and nothing else.
 *
 * Callables are non-streaming today. firebase-functions is pinned at ^5.1.1 and
 * callable streaming needs v6+, which would mean migrating ~70 gen1 exports in a
 * 6k-line monolith. Replies are one to three sentences by construction, so the
 * wait is short enough for a typing indicator.
 *
 * Reads go direct to Firestore under the security rules; writes go through
 * callables, because the server has to apply the environment gate, the rate
 * limiter and the safety filter before anything is persisted.
 */

import { httpsCallable } from 'firebase/functions';
import {
  collection, doc, getDocs, limit, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { functions, db } from '../firebase';
import type {
  FeedbackChatBootstrap,
  FeedbackConversationSummary,
  FeedbackMessage,
  FeedbackRating,
  SendFeedbackMessageResult,
} from '../types/feedbackChat';

const CONVERSATIONS = 'feedback_conversations';
const MESSAGES = 'messages';

// ------------------------------------------------------------------ callables

const bootstrapCallable = httpsCallable<Record<string, never>, FeedbackChatBootstrap>(
  functions, 'feedbackChatBootstrap',
);

const acknowledgeCallable = httpsCallable<{ disclaimerVersion: string }, { ok: boolean; version: string }>(
  functions, 'acknowledgeFeedbackDisclaimer',
);

const startCallable = httpsCallable<
{ openedFromPath?: string | null; appVersion?: string | null },
{ conversationId: string }
>(functions, 'startFeedbackConversation');

const sendCallable = httpsCallable<
{ conversationId: string; text: string; clientMessageId: string; openedFromPath?: string | null },
SendFeedbackMessageResult
>(functions, 'sendFeedbackMessage');

const rateCallable = httpsCallable<
{ conversationId: string; messageId: string; rating: FeedbackRating },
{ ok: boolean }
>(functions, 'rateFeedbackMessage');

const escalateCallable = httpsCallable<
{ conversationId: string; note?: string | null },
{ ok: boolean; content: string }
>(functions, 'escalateFeedbackConversation');

const closeCallable = httpsCallable<{ conversationId: string }, { ok: boolean; status: string }>(
  functions, 'closeFeedbackConversation',
);

// -------------------------------------------------------------------- public

export async function fetchBootstrap(): Promise<FeedbackChatBootstrap> {
  const result = await bootstrapCallable({});
  return result.data;
}

export async function acknowledgeDisclaimer(disclaimerVersion: string): Promise<void> {
  await acknowledgeCallable({ disclaimerVersion });
}

export async function startConversation(openedFromPath?: string): Promise<string> {
  const result = await startCallable({
    openedFromPath: openedFromPath || null,
    appVersion: process.env.REACT_APP_VERSION || null,
  });
  return result.data.conversationId;
}

export async function sendMessage(params: {
  conversationId: string;
  text: string;
  clientMessageId: string;
  openedFromPath?: string;
}): Promise<SendFeedbackMessageResult> {
  const result = await sendCallable({
    conversationId: params.conversationId,
    text: params.text,
    clientMessageId: params.clientMessageId,
    openedFromPath: params.openedFromPath || null,
  });
  return result.data;
}

export async function rateMessage(
  conversationId: string, messageId: string, rating: FeedbackRating,
): Promise<void> {
  await rateCallable({ conversationId, messageId, rating });
}

export async function escalateConversation(conversationId: string, note?: string): Promise<string> {
  const result = await escalateCallable({ conversationId, note: note || null });
  return result.data.content;
}

export async function closeConversation(conversationId: string): Promise<void> {
  await closeCallable({ conversationId });
}

// ---------------------------------------------------------------------- reads

/**
 * Live message stream for one conversation.
 *
 * onSnapshot rather than a fetch: the assistant reply is written server-side
 * after the callable returns its own copy, and a live subscription keeps the
 * two from racing. Same idiom as MemberRail's unread-count subscription.
 */
export function subscribeToMessages(
  conversationId: string,
  onChange: (messages: FeedbackMessage[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(doc(db, CONVERSATIONS, conversationId), MESSAGES),
    orderBy('createdAt', 'asc'),
  );

  return onSnapshot(
    q,
    (snap) => {
      onChange(snap.docs.map((d) => toMessage(d.id, d.data())));
    },
    (error) => {
      if (onError) onError(error as Error);
    },
  );
}

/** The member's own recent conversations, for the history list. */
export async function fetchConversations(
  uid: string, max = 10,
): Promise<FeedbackConversationSummary[]> {
  const q = query(
    collection(db, CONVERSATIONS),
    where('ownerUid', '==', uid),
    orderBy('lastMessageAt', 'desc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, any>;
    return {
      id: d.id,
      status: data.status || 'open',
      startedAt: toDate(data.startedAt),
      lastMessageAt: toDate(data.lastMessageAt),
      userMessageCount: data.userMessageCount || 0,
      escalationState: data.escalation?.state || 'none',
    };
  });
}

// --------------------------------------------------------------------- mapping

function toMessage(id: string, data: Record<string, any>): FeedbackMessage {
  return {
    id,
    role: data.role || 'assistant',
    // Default to approved_copy rather than ai for an unknown value: mislabelling
    // server copy as AI-authored is the more misleading of the two errors.
    authoredBy: data.authoredBy || 'approved_copy',
    content: data.content || '',
    createdAt: toDate(data.createdAt),
    card: data.card || null,
    rating: data.rating || null,
    copyKey: data.copyKey || null,
  };
}

function toDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

/** Client-side idempotency key, so a retry cannot duplicate a message. */
export function newClientMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
